import { Task } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import axios, { CancelToken } from 'axios'
import { LocationChangeAction } from 'connected-react-router'
import { matchPath } from 'react-router'
import { State } from '../store'
import { GetBalanceResult } from '../components/Balance/types'
import {
  loadable,
  memoed,
  PayloadAction,
  Action,
  Workspace,
  waitForWorkspace,
} from '@library/react-toolkit'

export function* takeLatestByNym<A extends PayloadAction<any, any>>(
  actionType: A['type'] | A['type'][],
  selector: (_: A['payload']) => string,
  worker: (_: A, _t: CancelToken) => any
) {
  const tasks: Record<string, Task> = {}
  while (true) {
    const a: A = yield effects.take(actionType)
    const nym = selector(a.payload)
    if (tasks[nym]) {
      yield effects.cancel(tasks[nym])
    }
    // @ts-ignore
    const task = yield effects.fork(withRequestCancellation(worker), a)
    tasks[nym] = task
  }
}

export const withRequestCancellation = <A extends Action<string>>(
  worker: (_: A, _t: CancelToken) => any
) =>
  function* (a: A) {
    const cancelSource = axios.CancelToken.source()
    try {
      yield worker(a, cancelSource.token)
    } finally {
      // @ts-ignore
      if (yield effects.cancelled()) {
        yield effects.call(cancelSource.cancel)
      }
    }
  }

export function* takeLatest<A extends Action<string>>(
  a: effects.ActionPattern<A>,
  worker: (_: A, _t: CancelToken) => any
) {
  yield effects.takeLatest(a, withRequestCancellation(worker))
}

const isLocationChangeAction = (a: Action<string>): a is LocationChangeAction =>
  a.type === '@@router/LOCATION_CHANGE'

export const matchPathGuard =
  (path: string): effects.ActionPattern<LocationChangeAction> =>
  <A extends Action<string>>(a: A) =>
    isLocationChangeAction(a) &&
    !!matchPath(a.payload.location.pathname, {
      path,
      exact: true,
    })

export function* waitForNymID() {
  const a: State['balance'] = yield effects.select((s: State) => s.balance)
  const memo = memoed.extract(a).userNymID

  if (!!memo) {
    return memo
  }

  while (true) {
    const action: GetBalanceResult = yield effects.take(
      'BALANCE/get_balance_result' as GetBalanceResult['type']
    )
    if (loadable.isOk(action.payload)) {
      return action.payload.value.userNymID
    }
  }
}

export function* waitForOrganization() {
  let ws: Workspace = { type: 'personal' }
  while (ws.type === 'personal') {
    ws = yield waitForWorkspace()
  }
  return ws.organization
}
